import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, Typography, theme, Card, Button } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { LlmCallLogDto, LlmCallStatus } from '@wellverse/shared-ts';
import { Field } from '../../../components/Field';

const statusColors: Record<LlmCallStatus, string> = {
  [LlmCallStatus.SUCCESS]: '#52c41a',
  [LlmCallStatus.FAILED]: '#ff4d4f',
  [LlmCallStatus.TIMEOUT]: '#fa8c16',
  [LlmCallStatus.PENDING]: '#898989',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

function formatJson(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
}

export const LlmCallLogShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<LlmCallLogDto>({ resource: 'llm-call-logs' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const record = item as LlmCallLogDto & {
    providerName?: string;
    promptName?: string;
  };

  return (
    <Show resource="llm-call-logs">
      <div style={{ padding: '8px 0' }}>
        <Field label="ID">{record.id}</Field>
        <Field label="Provider">{record.providerName || record.providerId || '—'}</Field>
        <Field label="Model">{record.model}</Field>
        <Field label="Endpoint">{record.endpoint || '—'}</Field>
        <Field label="Prompt">{record.promptName || record.promptVersionId || '—'}</Field>
        <Field label="Status">
          <Tag style={tagStyle(statusColors[record.status] || '#898989')}>
            {record.status}
          </Tag>
        </Field>
        <Field label="Latency">{record.latencyMs !== undefined ? `${record.latencyMs}ms` : '—'}</Field>
        <Field label="Input Tokens">{record.inputTokens ?? '—'}</Field>
        <Field label="Output Tokens">{record.outputTokens ?? '—'}</Field>
        <Field label="Total Tokens">{record.totalTokens ?? '—'}</Field>
        {record.errorMessage && (
          <Field label="Error">
            <Typography.Text type="danger">{record.errorMessage}</Typography.Text>
          </Field>
        )}
        {record.requestSummary && (
          <Field label="Request Summary">
            <div
              style={{
                background: token.colorBgLayout,
                padding: 12,
                borderRadius: 8,
                marginTop: 4,
                overflow: 'auto',
                maxHeight: 480,
              }}
            >
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  fontSize: 12,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  lineHeight: 1.5,
                }}
              >
                {formatJson(record.requestSummary)}
              </pre>
            </div>
          </Field>
        )}
        {record.responseSummary && (
          <Field label="Response Summary">
            <div
              style={{
                background: token.colorBgLayout,
                padding: 12,
                borderRadius: 8,
                marginTop: 4,
                overflow: 'auto',
                maxHeight: 480,
              }}
            >
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  fontSize: 12,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  lineHeight: 1.5,
                }}
              >
                {formatJson(record.responseSummary)}
              </pre>
            </div>
          </Field>
        )}
        <Field label="Created At">{new Date(record.createdAt).toLocaleString()}</Field>
        {record.metadata && typeof record.metadata === 'object' && (record.metadata as any).entityId && (
          <Card title="Linked Visual Entity" size="small" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{(record.metadata as any).entityId}</span>
              <a href={`/visual-entities/show/${(record.metadata as any).entityId}`}>
                <Button type="link" size="small" icon={<LinkOutlined />}>View Entity</Button>
              </a>
            </div>
          </Card>
        )}
      </div>
    </Show>
  );
};
